/* eslint-disable */

const RISK_PROFILES = [
  {
    id: 'aggressive',
    label: 'Aggressive',
    shortLabel: 'Aggressive',
    min: 0,
    max: 30,
    color: '#D93F3F',
    description:
      'Aggressive risk profile. Lower debt allocation supports higher equity exposure and greater tolerance for market volatility.',
  },
  {
    id: 'moderate',
    label: 'Moderate',
    shortLabel: 'Moderate',
    min: 30,
    max: 60,
    color: '#D99E16',
    description:
      'Moderate risk profile. Balanced debt allocation supports a mix of growth and stability.',
  },
  {
    id: 'conservative',
    label: 'Conservative',
    shortLabel: 'Conservative',
    min: 60,
    max: 100,
    color: '#4F6814',
    description:
      'Conservative risk profile. Higher debt allocation prioritizes stability and capital preservation.',
  },
];

export const getRiskProfile = (debtCashAllocation) => {
  const allocation = Math.max(0, Math.min(100, Number(debtCashAllocation) || 0));

  // Debt ≤30: Aggressive | 30 < Debt < 60: Moderate | Debt ≥60: Conservative
  if (allocation <= 30) {
    return { ...RISK_PROFILES[0], allocation };
  }
  if (allocation < 60) {
    return { ...RISK_PROFILES[1], allocation };
  }
  return { ...RISK_PROFILES[2], allocation };
};

const CustomerRiskProfile = ({ assets = [] }) => {
  const debtAllocation =
    assets?.find((asset) => asset.category === 'Debt')?.percentage ?? 0;

  const profile = getRiskProfile(debtAllocation);
  // Flipped meter: left = Conservative (high debt), right = Aggressive (low debt)
  const meterBands = [...RISK_PROFILES].reverse();
  const markerPosition = Math.max(1.5, Math.min(98.5, 100 - (Number(debtAllocation) || 0)));

  return (
    <div className="fabits-risk-profile-card">
      <div className="fabits-risk-profile-header">
        <div>
          <p className="fabits-risk-profile-eyebrow">
            Risk Profile
          </p>
          <div className="fabits-risk-profile-title-row">
            <h3
              className="fabits-risk-profile-title"
              style={{ '--risk-profile-color': profile.color }}
            >
              {profile.label}
            </h3>
          </div>
          <p className="fabits-risk-profile-description">
            {profile.description}
          </p>
        </div>
        <div className="fabits-risk-profile-score">
          <span>{Number(debtAllocation).toFixed(2)}%</span>
          <small>Debt allocation</small>
        </div>
      </div>

      <div className="fabits-risk-profile-meter-section">
        {/* Arrow marker */}
        <div className="fabits-risk-profile-marker-track">
          <div
            className="fabits-risk-profile-marker"
            style={{ left: `${markerPosition}%` }}
          >
            <span
              className="fabits-risk-profile-marker-label"
              style={{ backgroundColor: profile.color }}
            >
              You
            </span>
            <svg
              width="12"
              height="8"
              viewBox="0 0 12 8"
              fill="none"
              aria-hidden="true"
            >
              <path d="M6 8L0 0H12L6 8Z" fill={profile.color} />
            </svg>
          </div>
        </div>

        {/* Segmented risk meter */}
        <div
          className="fabits-risk-profile-meter"
          role="meter"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Number(debtAllocation)}
          aria-label={`Risk profile: ${profile.label}`}
        >
          {meterBands.map((band) => {
            const width = band.max - band.min;
            const isActive = band.id === profile.id;
            const isAggressive = band.id === 'aggressive';
            const isConservative = band.id === 'conservative';
            return (
              <div
                key={band.id}
                className={`fabits-risk-profile-band ${isActive ? 'is-active' : ''}`}
                style={{
                  '--risk-profile-band': band.color,
                  width: `${width}%`,
                }}
                title={
                  isAggressive
                    ? `${band.label} (≤${band.max}%)`
                    : isConservative
                      ? `${band.label} (≥${band.min}%)`
                      : `${band.label} (${band.min}–${band.max}%)`
                }
              />
            );
          })}
        </div>

        {/* Scale labels — widths match risk bands */}
        <div className="fabits-risk-profile-labels">
          {meterBands.map((band) => (
            <span
              key={band.id}
              className={band.id === profile.id ? 'is-active' : ''}
              style={{ width: `${band.max - band.min}%` }}
            >
              {band.shortLabel}
            </span>
          ))}
        </div>

        <p className="fabits-risk-profile-footnote">
          Derived from your Debt allocation. Higher debt share indicates a more conservative profile.
        </p>
      </div>
    </div>
  );
};

export default CustomerRiskProfile;
